
import React, { useState, useEffect } from 'react';
import { UserProfile } from '../../types';
import { 
    Loader2, X, Upload, Sparkles, Image as ImageIcon, Send, 
    CheckSquare, Square, ArrowLeft, Trash2 
} from 'lucide-react';

interface MediaItem {
    id: number;
    url: string;
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSelectMedia: (item: MediaItem) => void;
    currentUser: UserProfile;
    contextName: string; // For AI prompt
}

const slugify = (str: string) => {
    str = str.replace(/^\s+|\s+$/g, ''); // trim
    str = str.toLowerCase();
    const from = "àáäâèéëêìíïîòóöôùúüûñç·/_,:;ąćęłńóśźż";
    const to   = "aaaaeeeeiiiioooouuuunc------acelnoszz";
    for (let i = 0, l = from.length; i < l; i++) {
        str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
    }
    str = str.replace(/[^a-z0-9 -]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
    return str;
};

const MediaLibraryModal: React.FC<Props> = ({ isOpen, onClose, onSelectMedia, currentUser, contextName }) => {
    const [mediaLibrary, setMediaLibrary] = useState<MediaItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    
    const [generateView, setGenerateView] = useState<'closed' | 'form' | 'generating' | 'results'>('closed');
    const [numImages, setNumImages] = useState(1);
    const [sceneBrief, setSceneBrief] = useState('');
    const [generatedImages, setGeneratedImages] = useState<(string | MediaItem)[]>([]);
    const [isSavingGenerated, setIsSavingGenerated] = useState<number | null>(null);

    useEffect(() => {
        if (isOpen) {
            fetchMedia();
        } else {
            setGenerateView('closed');
            setGeneratedImages([]);
        }
    }, [isOpen]);

    useEffect(() => {
        if (generateView === 'form') {
            setSceneBrief(`A symbolic image representing the theme: ${contextName}`);
        }
    }, [generateView, contextName]);

    const fetchMedia = async () => {
        setIsLoading(true);
        try {
            const res = await fetch('/api/cms/media', { headers: { 'x-admin-phone': currentUser.phone } });
            if (res.ok) setMediaLibrary(await res.json());
        } catch (e) { console.error("Failed to load media library", e); }
        finally { setIsLoading(false); }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        
        setIsUploading(true);

        const uploadPromises = Array.from(files).map((file: File) => {
            return new Promise<void>(async (resolve) => {
                const dataUrl = await new Promise<string>(r => {
                    const reader = new FileReader();
                    reader.onload = () => r(reader.result as string);
                    reader.readAsDataURL(file);
                });

                try {
                    const res = await fetch('/api/cms/media', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                        body: JSON.stringify({ 
                            url: dataUrl,
                            type: 1,
                            context_slug: slugify(contextName || 'upload'),
                            original_filename: file.name
                        })
                    });
                    
                    if (!res.ok) {
                        const err = await res.json();
                        alert(`Upload Error: ${err.error}`);
                    }
                } catch (err) { console.error(err); }
                resolve();
            });
        });

        await Promise.all(uploadPromises);
        setIsUploading(false);
        e.target.value = ''; 
        fetchMedia(); // Refresh library
    };

    const handleGenerate = async () => {
        setGenerateView('generating');
        const prompt = sceneBrief; // Just send the scene brief
        
        try {
            const res = await fetch('/api/cms/generate-image', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify({ prompt, numberOfImages: numImages, contextName })
            });
            if (!res.ok) throw new Error('Failed to generate images.');
            
            const data = await res.json();
            setGeneratedImages(data.images);
            setGenerateView('results');

            // If images were batch-saved, refresh the main library view in the background
            if (data.saved) {
                fetchMedia();
            }
        } catch (error) {
            console.error(error);
            alert("Image generation failed. Please try again.");
            setGenerateView('form');
        }
    };
    
    const handleUploadGeneratedImage = async (base64Data: string, index: number) => {
        setIsSavingGenerated(index);
        try {
            const dataUrl = `data:image/jpeg;base64,${base64Data}`;
            const res = await fetch('/api/cms/media', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify({ 
                    url: dataUrl,
                    type: 1,
                    context_slug: slugify(contextName || 'ai-generated'),
                    original_filename: `${slugify(contextName || 'ai-generated')}.jpg`
                })
            });
            if (!res.ok) throw new Error('Failed to save generated image.');

            const newItem = await res.json();
            onSelectMedia(newItem);
            onClose();

        } catch (error) {
            console.error(error);
            alert('Failed to save image.');
        } finally {
            setIsSavingGenerated(null);
        }
    };

    const handleSelectSavedImage = (image: MediaItem) => {
        onSelectMedia(image);
        onClose();
    };
    
    if (!isOpen) return null;

    const renderGenerateView = () => (
        <div className="absolute inset-0 bg-mystic-box z-10 flex flex-col p-6 animate-fade-in">
            <div className="flex items-center gap-4 mb-6">
                <button onClick={() => setGenerateView('closed')} className="text-gray-400 hover:text-white">
                    <ArrowLeft />
                </button>
                <h3 className="text-lg font-bold text-white">Generate Image</h3>
            </div>

            {generateView === 'form' && (
                <div className="flex flex-col flex-grow gap-4 text-left">
                    <label className="text-sm font-bold text-gray-400">
                        Scene Description for "<span className="font-bold text-white">{contextName}</span>"
                    </label>
                    <textarea
                        value={sceneBrief}
                        onChange={(e) => setSceneBrief(e.target.value)}
                        className="w-full h-32 bg-mystic-inset border border-gray-700 p-3 rounded-lg text-white text-sm resize-none"
                        placeholder="e.g., A single black candle burning in a dark room, with wax dripping onto an old wooden table."
                    />

                    <div className="flex items-center justify-between mt-4">
                        <label className="text-sm font-bold text-gray-400">
                            Number of images:
                        </label>
                        <input 
                            type="number"
                            min="1" max="4"
                            value={numImages}
                            onChange={(e) => setNumImages(parseInt(e.target.value))}
                            className="w-20 bg-mystic-inset border border-gray-700 p-2 rounded text-white text-center text-lg"
                        />
                    </div>
                    
                    <button onClick={handleGenerate} className="bg-mystic-accent hover:bg-mystic-accentHover text-black font-bold py-3 px-6 rounded-lg flex items-center justify-center gap-2 w-full mt-auto">
                        <Send size={16}/> Generate
                    </button>
                </div>
            )}
            {generateView === 'generating' && (
                <div className="flex flex-col items-center justify-center flex-grow gap-4 text-center text-gray-400">
                    <Loader2 className="animate-spin w-10 h-10 text-mystic-accent" />
                    <p>Generating images... this may take a moment.</p>
                </div>
            )}
            {generateView === 'results' && (
                 <div className="flex-grow overflow-y-auto">
                    <p className="text-sm text-gray-400 mb-4">Select an image to save and use:</p>
                    <div className="grid grid-cols-2 gap-4">
                        {generatedImages.map((img, index) => {
                            const isSaved = typeof img === 'object' && img !== null && 'id' in img;
                            const imgSrc = isSaved ? (img as MediaItem).url : `data:image/jpeg;base64,${img}`;

                            return (
                                <button 
                                    key={index} 
                                    onClick={() => {
                                        if (isSaved) {
                                            handleSelectSavedImage(img as MediaItem);
                                        } else {
                                            handleUploadGeneratedImage(img as string, index);
                                        }
                                    }}
                                    disabled={!isSaved && isSavingGenerated !== null}
                                    className="aspect-video bg-black rounded-lg overflow-hidden relative group border-2 border-transparent hover:border-mystic-accent disabled:opacity-50"
                                >
                                    <img src={imgSrc} alt={`Generated image ${index + 1}`} className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        {!isSaved && isSavingGenerated === index ? (
                                            <Loader2 className="animate-spin text-white w-8 h-8"/>
                                        ) : (
                                            <span className="text-white font-bold bg-black/50 px-3 py-1 rounded">
                                                 {isSaved ? 'Use' : 'Save & Use'}
                                            </span>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                 </div>
            )}
        </div>
    );

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-mystic-box border border-gray-700 w-full max-w-4xl h-[80vh] rounded-xl shadow-2xl flex flex-col relative overflow-hidden">
                {generateView !== 'closed' && renderGenerateView()}

                <div className="p-4 border-b border-gray-700 flex justify-between items-center bg-mystic-bg rounded-t-xl">
                    <h3 className="text-lg font-bold text-white">Media Library</h3>
                    <div className="flex items-center gap-3">
                        <button onClick={() => setGenerateView('form')} className="bg-mystic-btn hover:bg-white/90 text-white hover:text-black px-4 py-1.5 rounded flex items-center gap-2 text-xs font-bold transition-colors">
                            <Sparkles size={14} /> Generate Image
                        </button>
                        <label className={`bg-mystic-accent hover:bg-mystic-accentHover text-black px-4 py-1.5 rounded cursor-pointer flex items-center gap-2 text-xs font-bold transition-colors ${isUploading ? 'opacity-50' : ''}`}>
                            {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                            Upload New
                            <input type="file" accept="image/*" multiple className="hidden" onChange={handleFileUpload} disabled={isUploading} />
                        </label>
                        <button onClick={onClose}><X className="w-5 h-5 text-gray-500 hover:text-white"/></button>
                    </div>
                </div>
                
                <div className="flex-1 overflow-y-auto p-6 bg-mystic-inset">
                    {isLoading ? <Loader2 className="animate-spin mx-auto mt-10" /> : (
                        <div className="grid grid-cols-4 md:grid-cols-6 gap-4">
                            {mediaLibrary.map(item => (
                                <div 
                                    key={item.id} 
                                    onClick={() => onSelectMedia(item)}
                                    className="aspect-square bg-mystic-inset rounded cursor-pointer relative group overflow-hidden border-2 border-gray-800 hover:border-mystic-accent"
                                >
                                    <img src={item.url} alt="" className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MediaLibraryModal;
